import type { CalendarDate } from "./work-policy-application.types";

export interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

function isLeapYear(year: number) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number) {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

export function parseCalendarDate(value: string): CalendarDateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error("Invalid calendar date.");
  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3])
  };
  if (
    parts.year < 1 ||
    parts.month < 1 ||
    parts.month > 12 ||
    parts.day < 1 ||
    parts.day > daysInMonth(parts.year, parts.month)
  ) {
    throw new Error("Invalid calendar date.");
  }
  return parts;
}

export function formatCalendarDate(parts: CalendarDateParts): CalendarDate {
  if (
    !Number.isInteger(parts.year) ||
    !Number.isInteger(parts.month) ||
    !Number.isInteger(parts.day)
  ) {
    throw new Error("Invalid calendar date component.");
  }
  const value = `${String(parts.year).padStart(4, "0")}-${pad(
    parts.month
  )}-${pad(parts.day)}`;
  parseCalendarDate(value);
  return value;
}

export function formatKoreanCalendarDate(value: CalendarDate): string {
  const { year, month, day } = parseCalendarDate(value);
  return `${year}년 ${month}월 ${day}일`;
}

export function clampCalendarDate(
  year: number,
  month: number,
  requestedDay: number
): CalendarDate {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(requestedDay) ||
    year < 1 ||
    month < 1 ||
    month > 12 ||
    requestedDay < 1 ||
    requestedDay > 31
  ) {
    throw new Error("Invalid calendar date component.");
  }
  return formatCalendarDate({
    year,
    month,
    day: Math.min(requestedDay, daysInMonth(year, month))
  });
}

function nextCalendarDate(parts: CalendarDateParts, direction: 1 | -1) {
  if (direction === 1) {
    if (parts.day < daysInMonth(parts.year, parts.month)) {
      return { ...parts, day: parts.day + 1 };
    }
    if (parts.month < 12) return { year: parts.year, month: parts.month + 1, day: 1 };
    return { year: parts.year + 1, month: 1, day: 1 };
  }
  if (parts.day > 1) return { ...parts, day: parts.day - 1 };
  if (parts.month > 1) {
    const month = parts.month - 1;
    return { year: parts.year, month, day: daysInMonth(parts.year, month) };
  }
  const year = parts.year - 1;
  if (year < 1) throw new Error("Calendar date is out of range.");
  return { year, month: 12, day: 31 };
}

export function addCalendarDays(value: CalendarDate, days: number): CalendarDate {
  if (!Number.isInteger(days)) throw new Error("Calendar day offset must be an integer.");
  let parts = parseCalendarDate(value);
  const direction: 1 | -1 = days < 0 ? -1 : 1;
  for (let offset = 0; offset < Math.abs(days); offset += 1) {
    parts = nextCalendarDate(parts, direction);
  }
  return formatCalendarDate(parts);
}

export function calendarDateRange(
  startDate: CalendarDate,
  endDate: CalendarDate
): CalendarDate[] {
  parseCalendarDate(startDate);
  parseCalendarDate(endDate);
  if (startDate > endDate) throw new Error("Invalid calendar date range.");
  const dates: CalendarDate[] = [];
  let current = startDate;
  while (current <= endDate) {
    dates.push(current);
    current = addCalendarDays(current, 1);
  }
  return dates;
}
